import { Helmet } from '@dr.pogodin/react-helmet';
import { useApiRead } from 'api-read-hook';
import { compareAsc, compareDesc, parseISO } from 'date-fns';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import { PageError, PageLoading, Text } from '../../components';
import { API_ROOT } from '../../config';
import { useSelectedCompany } from '../../contexts/ClientDataContext';
import { useAuthUser } from '../../hooks';
import { formatAsDateTime } from '../../util';

export default function DataPage() {
  const { companySlug, dataSetId } = useParams<{
    companySlug: string;
    dataSetId: string;
  }>();
  const authUser = useAuthUser();
  const selectedCompany = useSelectedCompany();

  type DataSorter = { sortDateField?: [string, 'ASC' | 'DESC'] };
  type DataResult = Record<string, unknown>;
  const api = useApiRead<{
    name: string;
    sorters: DataSorter[];
    results: DataResult[];
  }>(`/company-data/set/${companySlug}/${dataSetId}`);

  if (api.error) return <PageError {...api} />;
  if (!api.data) return <PageLoading />;

  const { name, sorters, results } = api.data;

  if (results.length === 0) return 'Zero records';

  function renderValue(v: unknown): React.ReactNode {
    if (typeof v === 'boolean')
      return v ? 'yes' : <Text style={styles.grey}>no</Text>;
    if (v == null) return '';
    if (v === 0) return <Text style={styles.grey}>0</Text>;
    const str = String(v);
    if (str.match(/\d{4}-\d{2}-\d{2} /)) {
      return formatAsDateTime(str);
    }
    return str;
  }

  const sorter = sorters[0];
  let sortFn;
  if (sorter.sortDateField) {
    const sortDateField = sorter.sortDateField;
    const dfn = sortDateField[1] === 'ASC' ? compareAsc : compareDesc;
    sortFn = (a: DataResult, b: DataResult) => {
      const aField = a[sortDateField[0]] as string | null;
      const bField = b[sortDateField[0]] as string | null;

      // Push null values to the end
      if (!aField && bField) return 1;
      if (aField && !bField) return -1;

      return dfn(parseISO(aField!), parseISO(bField!));
    };
  }
  const prepared = results.sort(sortFn);

  return (
    <>
      <Helmet>
        <title>{`${name} - Data Set | ${selectedCompany.name}`}</title>
      </Helmet>

      <html.div style={styles.top}>
        <Text as="h1" style={styles.heading}>
          Data Set: {name}
          <html.a
            href={`${API_ROOT}/company-data/set/${companySlug}/${dataSetId}.csv?jwt=${authUser.jwt}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.csvLink}
          >
            download csv
          </html.a>
        </Text>

        <html.div style={styles.filters}>
          <html.div />
          <Text as="p">
            {prepared.length} of {results.length} records
          </Text>
        </html.div>
      </html.div>

      <html.div style={styles.tableWrap}>
        <html.div style={styles.table}>
          <html.div style={styles.headerRow}>
            {Object.keys(results[0]).map((key) => (
              <Text key={key} as="p" style={styles.headerCell}>
                {key}
              </Text>
            ))}
          </html.div>
          {prepared.map((result) => {
            const entries = Object.entries(result);
            const cells = entries.map(([k, v]) => (
              <Text key={k} as="p" style={styles.cell}>
                {renderValue(v)}
              </Text>
            ));
            return (
              <html.div key={String(entries[0][1])} style={styles.bodyRow}>
                {cells}
              </html.div>
            );
          })}
        </html.div>
      </html.div>
    </>
  );
}

const styles = css.create({
  top: {
    padding: 30,
  },
  heading: {
    color: '#333',
  },
  csvLink: {
    color: '#0081ff',
    fontSize: 14,
    marginLeft: 25,
    fontWeight: 'normal',
  },
  filters: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  tableWrap: {
    overflowX: 'scroll',
    maxWidth: '87vw',
  },
  table: {
    backgroundColor: '#fff',
  },
  headerRow: {
    display: 'flex',
  },
  bodyRow: {
    display: 'flex',
    ':hover': {
      backgroundColor: 'rgba(96, 120, 255, 0.1)',
    },
  },
  headerCell: {
    flex: 1,
    padding: 15,
    fontSize: 14,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgba(41, 41, 64, 0.1)',
    color: 'rgb(51, 51, 79)',
  },
  cell: {
    flex: 1,
    padding: 15,
    fontSize: 14,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgba(41, 41, 64, 0.1)',
    color: 'rgb(92, 92, 108)',
  },
  grey: {
    color: '#b8b8d9',
  },
});
